// Admin user mutations: verify / block / unblock / send-reset / platform-role.
//
// Invalidation follows the DealCardPage pattern (shared queryClient singleton):
// invalidate the admin root, then refetch active queries so the open page
// reflects the change at once.
import { useMutation } from '@tanstack/react-query';

import { queryClient } from '../../../shared/query/queryClient.js';

import {
  ADMIN_QUERY_ROOT,
  blockAdminUser,
  sendAdminUserPasswordReset,
  setAdminUserPlatformRole,
  unblockAdminUser,
  verifyAdminUserEmail,
  type PlatformRole,
} from '../api/admin.client.js';

async function invalidateAdminQueries(): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: [ADMIN_QUERY_ROOT] });
  await queryClient.refetchQueries({ queryKey: [ADMIN_QUERY_ROOT], type: 'active' });
}

export function useAdminUsersMutations() {
  const verifyMutation = useMutation({
    mutationFn: (userId: string) => verifyAdminUserEmail(userId),
    onSuccess: invalidateAdminQueries,
  });

  const blockMutation = useMutation({
    mutationFn: (input: { userId: string; reason?: string }) =>
      blockAdminUser(input.userId, input.reason),
    onSuccess: invalidateAdminQueries,
  });

  const unblockMutation = useMutation({
    mutationFn: (userId: string) => unblockAdminUser(userId),
    onSuccess: invalidateAdminQueries,
  });

  const sendResetMutation = useMutation({
    mutationFn: (userId: string) => sendAdminUserPasswordReset(userId),
    onSuccess: invalidateAdminQueries,
  });

  const setPlatformRoleMutation = useMutation({
    mutationFn: (input: { userId: string; role: PlatformRole }) =>
      setAdminUserPlatformRole(input.userId, input.role),
    onSuccess: invalidateAdminQueries,
  });

  return {
    verifyMutation,
    blockMutation,
    unblockMutation,
    sendResetMutation,
    setPlatformRoleMutation,
  };
}
